/**
 * Portal embed: is this visit Suikamon running inside a web game portal's frame (CrazyGames,
 * itch.io)?
 *
 * A portal shows the game in an iframe of suikamon.com. Inside it the Suikamon sign-in window never
 * opens: OAuth providers refuse to be framed, and CrazyGames allows no sign-in but its own
 * (docs.crazygames.com/requirements/account-integration). On CrazyGames every sign-in button
 * opens Login with CrazyGames instead (auth/useCrazyGamesLogin.ts); on a portal with no login we
 * support, sign-in and sign-up are hidden and the visitor plays as a guest (auth/useOffersSignIn.ts).
 *
 * Two conditions, both required:
 *   1. the page is framed (`window.self !== window.top`), so a visitor on suikamon.com itself is
 *      never affected, whatever the URL says;
 *   2. the frame says it is a portal: the iframe URL carries `?portal=crazygames` (or `itch`), or
 *      the browser reports a portal ancestor (`location.ancestorOrigins`, Chrome and Safari).
 * The answer is kept in sessionStorage, so it survives in-app navigation after the query string
 * is gone. The host is matched, never the scheme: the iOS app frames from capacitor://.
 */

/** The query parameter a portal's iframe URL carries: `?portal=crazygames`. */
export const PORTAL_QUERY_PARAM = "portal";

/** The portals Suikamon knows how to run inside. */
export const PORTALS: Readonly<Record<string, { hosts: readonly string[] }>> = {
  crazygames: { hosts: ["crazygames.com"] },
  // itch.io pages frame their HTML games from *.itch.zone.
  itch: { hosts: ["itch.io", "itch.zone"] },
};

export type PortalName = string;

/** sessionStorage key holding the portal this visit was opened from. */
export const PORTAL_STORAGE_KEY = "suikamon.portal.v1";

function isPortalName(value: string | null): value is PortalName {
  return value !== null && Object.prototype.hasOwnProperty.call(PORTALS, value);
}

/** True when `hostname` is one of the portal's hosts or a subdomain of one. */
export function isPortalHost(hostname: string, portal: PortalName): boolean {
  return PORTALS[portal].hosts.some((host) => hostname === host || hostname.endsWith(`.${host}`));
}

function isFramed(win: Window): boolean {
  try {
    return win.self !== win.top;
  } catch {
    // A cross-origin parent can make reading `top` throw: that is itself a frame.
    return true;
  }
}

function portalFromAncestors(win: Window): PortalName | null {
  const ancestors = win.location.ancestorOrigins;
  if (!ancestors) return null;
  for (let i = 0; i < ancestors.length; i += 1) {
    let hostname: string;
    try {
      hostname = new URL(ancestors[i]).hostname;
    } catch {
      continue;
    }
    for (const portal of Object.keys(PORTALS)) {
      if (isPortalHost(hostname, portal)) return portal;
    }
  }
  return null;
}

function readStored(win: Window): PortalName | null {
  try {
    const stored = win.sessionStorage.getItem(PORTAL_STORAGE_KEY);
    return isPortalName(stored) ? stored : null;
  } catch {
    return null;
  }
}

function store(win: Window, portal: PortalName): void {
  try {
    win.sessionStorage.setItem(PORTAL_STORAGE_KEY, portal);
  } catch {
    // Blocked storage: the query string still answers on the landing page.
  }
}

/** Work out the portal for a window. Exported for tests; the app uses {@link getPortal}. */
export function detectPortal(win: Window): PortalName | null {
  if (!isFramed(win)) return null;
  const fromQuery = new URLSearchParams(win.location.search).get(PORTAL_QUERY_PARAM);
  const portal = (isPortalName(fromQuery) ? fromQuery : null) ?? portalFromAncestors(win) ?? readStored(win);
  if (portal) store(win, portal);
  return portal;
}

let cached: PortalName | null | undefined;

/** The portal this visit runs inside, or null on suikamon.com itself. */
export function getPortal(): PortalName | null {
  if (cached === undefined) cached = typeof window === "undefined" ? null : detectPortal(window);
  return cached;
}

/**
 * True inside a portal's frame. Every Suikamon sign-in and sign-up surface checks this and
 * stays hidden, because the portal allows no sign-in of ours.
 */
export function isPortalEmbed(): boolean {
  return getPortal() !== null;
}

/** Test seam: forget the cached answer. */
export function resetPortalForTests(): void {
  cached = undefined;
}
